import React, { useEffect, useState } from "react";
import { AlertTriangle, Clock, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";

const MR_MONTHS = ["जानेवारी", "फेब्रुवारी", "मार्च", "एप्रिल", "मे", "जून", "जुलै", "ऑगस्ट", "सप्टेंबर", "ऑक्टोबर", "नोव्हेंबर", "डिसेंबर"];

function fmtIST(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const mm = m < 10 ? `0${m}` : m;
  return `${d.getDate()} ${MR_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h}:${mm} ${ap}`;
}

export default function DeadlineBanner() {
  const [state, setState] = useState(null);
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => api.getDeadline().then((d) => { if (alive) { setState(d); setSecs(d?.secondsLeft ?? 0); } }).catch(() => {});
    load();
    const poll = setInterval(load, 60000);
    return () => { alive = false; clearInterval(poll); };
  }, []);

  useEffect(() => {
    if (!state?.configured || state.passed) return;
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [state]);

  if (!state || (!state.configured && !state.notStarted)) return null;

  const dateStr = fmtIST(state.effectiveDeadline);
  const startStr = fmtIST(state.startDate);
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const ss = secs % 60;
  const urgent = !state.passed && secs <= 72 * 3600;

  let tone, Icon, content;
  if (state.notStarted) {
    tone = "bg-[#1b5e3f] text-white";
    Icon = Clock;
    content = <span><b>अर्ज अद्याप सुरू झालेले नाहीत.</b> अर्ज <b>{startStr}</b> पासून सुरू होतील.</span>;
  } else if (state.passed) {
    tone = "bg-slate-800 text-slate-100";
    Icon = AlertTriangle;
    content = <span><b>अर्ज करण्याची मुदत संपली आहे.</b> ({dateStr})</span>;
  } else if (urgent) {
    tone = "bg-[#c62828] text-white animate-pulse-slow";
    Icon = AlertTriangle;
    const totalHours = Math.floor(secs / 3600);
    content = (
      <span>
        {state.isExtended && <b className="mr-1 rounded bg-white/25 px-1.5 py-0.5">मुदतवाढ</b>}
        <b>अर्ज करण्यासाठी शेवटचे {totalHours} तास शिल्लक!</b>
        <span className="ml-2 font-latin tabular-nums">{String(hours).padStart(2, "0")}:{String(mins).padStart(2, "0")}:{String(ss).padStart(2, "0")}</span>
        <span className="ml-2 opacity-90">· अंतिम तारीख: {dateStr}</span>
      </span>
    );
  } else if (state.isExtended) {
    tone = "bg-[#0d5c3f] text-white";
    Icon = CalendarCheck;
    content = <span><b>अंतिम तारखेत वाढ:</b> नवीन अंतिम तारीख <b>{dateStr}</b> — फक्त {days} दिवस शिल्लक</span>;
  } else {
    tone = "bg-[#1b5e3f] text-white";
    Icon = Clock;
    content = <span><b>अर्ज करण्याची अंतिम तारीख: {dateStr}</b> — फक्त {days} दिवस शिल्लक</span>;
  }

  return (
    <div data-testid="deadline-banner" className={`sticky top-0 z-[60] w-full ${tone}`}>
      <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-4 py-2 text-center text-[13px] sm:text-[13.5px]">
        <Icon className="h-4 w-4 shrink-0" />
        {content}
      </div>
    </div>
  );
}
